import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Skeleton } from 'primereact/skeleton';
import OpportunityTileComponent from './components/OpportunityTile/OpportunityTile';
import SparklinePanel from './components/SparklinePanel/SparklinePanel';
import COTDistributionChart from './components/COTDistributionChart/COTDistributionChart';
import MarketStructureChart from './components/MarketStructureChart/MarketStructureChart';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './OpportunityHero.styles.scss';
import { OpportunityHeroProps } from './OpportunityHero.type';
import { useOpportunityHero } from './useOpportunityHero.logic';
import BubbleChart from './components/SparklinePanel/Bubblechart';
import MarketInsights from '../MarketInsights/MarketInsights';
import ContractTypesSection from './components/ContractTypesSection/ContractTypesSection';

const OpportunityHero: React.FC<OpportunityHeroProps> = observer(
  ({ tiles, loeStatus, sparklineData, onDataUpdate }) => {
    const {
      selectedPeriod,
      showCustomDateModal,
      tempFromDate,
      tempToDate,
      periodMonths,
      handlePeriodChange,
      handleApply,
      handleCancel,
      handleDropdownFocus,
      getHeadingText,
      currentTiles,
      currentLoeStatus,
      currentSparklineData,
      isLoading: _isLoading,
      periodOptions,
      setTempFromDate,
      setTempToDate,
    } = useOpportunityHero(
      productAnalysisStore,
      tiles,
      loeStatus,
      sparklineData,
      onDataUpdate || (() => {})
    );

    // Separate contract types tile from other tiles
    const { regularTiles, contractTypesTile } = useMemo(() => {
      const contractTypes = currentTiles.find(
        tile => tile.label?.trim().toLowerCase() === 'contract types'
      );
      const regular = currentTiles.filter(
        tile => tile.label?.trim().toLowerCase() !== 'contract types'
      );
      return {
        regularTiles: regular,
        contractTypesTile: contractTypes || null,
      };
    }, [currentTiles]);

    return (
      <section className="opportunity-hero">
        <div className="opportunity-hero-header">
          <h2 className="opportunity-hero-title">{getHeadingText()}</h2>

          <div className="date-picker-container">
            <div className="period-selector">
              <Dropdown
                value={selectedPeriod}
                options={periodOptions}
                onChange={handlePeriodChange}
                onFocus={handleDropdownFocus}
                placeholder="Select Period"
                className="period-dropdown"
              />
            </div>

            {showCustomDateModal && (
              <div className="custom-date-pickers" onClick={e => e.stopPropagation()}>
                <div className="date-picker-wrapper">
                  <label htmlFor="from-date">From Date:</label>
                  <Calendar
                    id="from-date"
                    value={tempFromDate}
                    onChange={e => setTempFromDate(e.value as Date)}
                    dateFormat="mm-dd-yy"
                    placeholder="Select From Date"
                    className="date-picker"
                    showIcon
                    panelClassName="custom-datepicker-panel"
                    inputId="from-date-input"
                  />
                </div>
                <div className="date-picker-wrapper">
                  <label htmlFor="to-date">To Date:</label>
                  <Calendar
                    id="to-date"
                    value={tempToDate}
                    onChange={e => setTempToDate(e.value as Date)}
                    dateFormat="mm-dd-yy"
                    placeholder="Select To Date"
                    className="date-picker"
                    minDate={tempFromDate || undefined}
                    showIcon
                    panelClassName="custom-datepicker-panel"
                    inputId="to-date-input"
                  />
                </div>
                <div className="date-picker-actions">
                  <Button
                    label="Apply"
                    onClick={handleApply}
                    className="apply-button"
                    disabled={!tempFromDate || !tempToDate}
                  />
                  <Button
                    label="Cancel"
                    onClick={handleCancel}
                    className="cancel-button"
                    outlined
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tiles Section - Shows skeleton when loading tiles */}
        {productAnalysisStore.isTilesLoading ? (
          <div className="opportunity-tiles-skeleton">
            <Skeleton width="100%" height="8rem" borderRadius="0.75rem" />
          </div>
        ) : (
          <div className="opportunity-tiles">
            {regularTiles.map((tile, index) => (
              <OpportunityTileComponent key={index} tile={tile} period={selectedPeriod}/>
            ))}
          </div>
        )}

        {/* Contract Types Section */}
        {!productAnalysisStore.isTilesLoading && (
          <ContractTypesSection contractTypesTile={contractTypesTile} />
        )}

        {/* Charts Section - Each chart handles its own loading state */}
        <div className="charts-container">
          {/* Sparkline Panel - Shows skeleton when loading sparklines */}
          {productAnalysisStore.isSparklinesLoading ? (
            <div className="sparkline-panel-skeleton">
              <Skeleton width="100%" height="18rem" borderRadius="0.75rem" />
            </div>
          ) : (
            <SparklinePanel
              sparklineData={currentSparklineData}
              loeStatus={currentLoeStatus}
              periodMonths={periodMonths}
            />
          )}

          {/* Market Structure Chart - Has its own loading state */}
          <MarketStructureChart />

          {/* COT Distribution Chart - Has its own loading state */}
          <COTDistributionChart
            data={productAnalysisStore.cotDistributionData}
            isLoading={productAnalysisStore.isCOTDistributionLoading}
            error={productAnalysisStore.cotDistributionError}
          />

          {/* Bubble Chart - Uses medical supply loading state */}
          <BubbleChart
            data={productAnalysisStore.medicalSupplyCompaniesData}
            isLoading={productAnalysisStore.isMedicalSupplyLoading}
          />
        </div>

        <MarketInsights />
      </section>
    );
  }
);

OpportunityHero.displayName = 'OpportunityHero';

export default OpportunityHero;
